import type { SupabaseClient } from '@supabase/supabase-js';
import {
  KF_ICON,
  KF_LOGO_GOECOMMERCE,
  KF_LOGO_KAUFLAND,
  kfFlagSvg,
} from '@vario-connect/frontend-design';
import {
  renderListingDetailPane,
  STATUS_PILL,
  STOREFRONT_LABELS,
  type ListingStatus,
} from '../shared/listing-detail-pane.js';

// ============================================================================
// Kaufland Lister — Inbox-Flow (analog Bestellungen-UI).
//
// Konzept:
//   * Die Inbox kaufland.listings wird ausschliesslich vom Vario-Slot befuellt:
//     wenn der User im Vario-UI im Verkaufskanal-Slot eine Kaufland-Kategorie
//     assigniert, schreibt der Slot via RPC kaufland_upsert_listing_from_slot
//     eine neue Row. Kein Hintergrund-Scan, kein Cron, kein "Aus Vario
//     nachladen"-Button — die User-Aktion im Slot ist der einzige Schreibpfad.
//   * Diese Maske ist NUR der Master: Inbox-Liste, Filter, Suche, Pager.
//     Das Detail rechts (Tabs Mapping/Bilder/Optional/Preview + Aufschalt-
//     Panel) rendert das geteilte Modul views/shared/listing-detail-pane.ts —
//     dasselbe Pane laeuft auch im Vario-Slot (listing-status, Configure-Modus).
//   * Status-Lifecycle: pending → mapping → mapped → queued → published.
// ============================================================================

interface KauflandListCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  supabaseUrl: string;
}

/** Slot-Ctx wie im Vario-Sales-Channels-Tab (analog OffersSlotCtx). */
interface KauflandListSlotCtx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind?: string };
  supabaseUrl: string;
}

/** Standalone-Einstieg: Lister als eigener `settings.sales-channels.{appId}.tabs`-Tab
 *  (→ /#/list). Rendert die Maske im kf-page-Design (apphead + Filterbar + Master-Liste),
 *  Detail-Pane bleibt das geteilte renderListingDetailPane. */
export function renderKauflandList(ctx: KauflandListSlotCtx): void {
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = '';
  void renderKauflandListTab(
    ctx.contentEl,
    { supabase: ctx.supabase, session: ctx.session, supabaseUrl: ctx.supabaseUrl },
    { standalone: true },
  );
}

/** ListingStatus → kf-page-Status-Pill-Klasse (Punkt + Text). Labels kommen aus
 *  STATUS_PILL (geteilt mit dem Detail-Pane), die Farbe aus diesen Klassen. */
const STATUS_CLS: Record<ListingStatus, string> = {
  pending: 'st-pending',
  mapping: 'st-mapping',
  mapped: 'st-mapped',
  queued: 'st-queued',
  published: 'st-published',
  error: 'st-error',
  archived: 'st-archived',
};

// Statische Inline-SVGs aus dem Design-Handoff "Kaufland Lister" (currentColor, 24×24).
const TAG_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><path d="M7 7h.01"/></svg>';
const WARN_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/></svg>';
const SPARK_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v4M12 18v4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M2 12h4M18 12h4"/></svg>';
const PG_FIRST =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m11 17-5-5 5-5M18 17l-5-5 5-5"/></svg>';
const PG_PREV =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m15 18-6-6 6-6"/></svg>';
const PG_NEXT =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m9 18 6-6-6-6"/></svg>';
const PG_LAST =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m13 17 5-5-5-5M6 17l5-5-5-5"/></svg>';

interface ListingRow {
  id: string;
  vario_article_id: string;
  vario_listing_id: string;
  vario_article_number: string | null;
  vario_article_name: string | null;
  storefront: string;
  vario_category_name: string | null;
  kaufland_category_id: number | null;
  status: ListingStatus;
  is_variant_parent: boolean;
  last_error: string | null;
  last_pushed_at: string | null;
  first_seen_at: string;
  updated_at: string;
  total_count: number;
  /** Aufgehydratet via per-Page-Lookup auf kaufland.listings (siehe
   *  hydrateModes()), weil die RPC kaufland_list_listings die Spalte nicht
   *  liefert. Fallback 'new', wenn der Lookup leer/failed. */
  mode?: 'new' | 'attach';
}

const STATUS_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '',          label: 'Alle (offen)' },
  { value: 'pending',   label: 'Neu' },
  { value: 'mapping',   label: 'In Bearbeitung' },
  { value: 'mapped',    label: 'Bereit' },
  { value: 'error',     label: 'Fehler' },
  { value: 'published', label: 'Gelistet' },
  { value: 'archived',  label: 'Archiviert' },
];

type ModeFilterValue = 'all' | 'new' | 'attach';

const MODE_FILTER_OPTIONS: Array<{ value: ModeFilterValue; label: string }> = [
  { value: 'all',    label: 'Alle Modi' },
  { value: 'new',    label: 'Neu listen' },
  { value: 'attach', label: 'Aufschalten' },
];

interface FilterState {
  storefront: string;
  status: string;
  /** Modus-Filter: 'all' = unfiltered, sonst 'new' | 'attach'. Clientseitig
   *  angewendet, weil das kaufland_list_listings-RPC den mode weder zurueckliefert
   *  noch als Filter akzeptiert. TODO (Migration 0116): RPC um _mode_filter
   *  erweitern, damit Pagination/Counts korrekt sind. */
  mode: ModeFilterValue;
  /** Frei-Such-Term ueber SKU/Name/Kategorie. Client-side Filter ueber die
   *  geladenen Rows (PAGE_SIZE), kein RPC-Roundtrip — der RPC ist nicht
   *  trivial erweiterbar und 50 Rows pro Page sind ausreichend filterbar. */
  search: string;
}

const PAGE_SIZE = 50;

export async function renderKauflandListTab(
  host: HTMLElement,
  ctx: KauflandListCtx,
  opts: { standalone?: boolean } = {},
) {
  const standalone = !!opts.standalone;
  host.innerHTML = renderShell(standalone);

  const root = host.querySelector<HTMLElement>('.kf-page')!;
  if (standalone) {
    const fit = () => { root.style.height = window.innerHeight + 'px'; };
    window.addEventListener('resize', fit);
    window.addEventListener('orientationchange', fit);
    fit();
  }

  const state: FilterState = { storefront: '', status: '', mode: 'all', search: '' };
  let activePage = 0;
  let activeListingId: string | null = null;
  let lastRows: ListingRow[] = [];

  // Storefront-Filter befüllen
  await populateStorefronts(ctx, host);

  // Event-Wiring -----------------------------------------------------------
  const sfSel = host.querySelector<HTMLSelectElement>('#klst-sf')!;
  const statusSel = host.querySelector<HTMLSelectElement>('#klst-status')!;
  const modeSel = host.querySelector<HTMLSelectElement>('#klst-mode')!;
  const pgFirst = host.querySelector<HTMLButtonElement>('#klst-pg-first')!;
  const pgPrev = host.querySelector<HTMLButtonElement>('#klst-pg-prev')!;
  const pgNext = host.querySelector<HTMLButtonElement>('#klst-pg-next')!;
  const pgLast = host.querySelector<HTMLButtonElement>('#klst-pg-last')!;

  // Status-Filter mit Optionen befüllen
  statusSel.innerHTML = STATUS_FILTER_OPTIONS.map((o) => `<option value="${o.value}">${o.label}</option>`).join('');
  // Modus-Filter mit Optionen befüllen
  modeSel.innerHTML = MODE_FILTER_OPTIONS.map((o) => `<option value="${o.value}">${o.label}</option>`).join('');

  // Filter-Change: activeListingId verwerfen, damit reloadList() automatisch
  // die erste Row der neuen Sicht selektiert (statt am vorigen Listing zu
  // kleben — das oft gar nicht mehr in der gefilterten Liste steht).
  sfSel.addEventListener('change', () => {
    state.storefront = sfSel.value;
    activePage = 0;
    activeListingId = null;
    void reloadList();
  });
  statusSel.addEventListener('change', () => {
    state.status = statusSel.value;
    activePage = 0;
    activeListingId = null;
    void reloadList();
  });
  modeSel.addEventListener('change', () => {
    state.mode = (modeSel.value as ModeFilterValue) || 'all';
    activePage = 0;
    activeListingId = null;
    void reloadList();
  });
  // Such-Filter: live ueber bereits geladene Rows. Reine DOM-Operation, kein RPC-Reload.
  const searchInput = host.querySelector<HTMLInputElement>('#klst-search')!;
  const searchClear = host.querySelector<HTMLButtonElement>('#klst-search-clear')!;
  const updateClearVis = () => { searchClear.classList.toggle('show', searchInput.value.length > 0); };
  searchInput.addEventListener('input', () => {
    state.search = searchInput.value;
    updateClearVis();
    applyClientSearchFilter(host, state.search);
  });
  searchClear.addEventListener('click', () => {
    searchInput.value = '';
    state.search = '';
    updateClearVis();
    applyClientSearchFilter(host, '');
    searchInput.focus();
  });
  pgFirst.addEventListener('click', () => { activePage = 0; void reloadList(); });
  pgPrev.addEventListener('click', () => { if (activePage > 0) { activePage -= 1; void reloadList(); } });
  pgNext.addEventListener('click', () => { activePage += 1; void reloadList(); });
  pgLast.addEventListener('click', () => {
    const total = lastRows[0]?.total_count ?? 0;
    activePage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
    void reloadList();
  });

  await reloadList();

  // Delegiert an das geteilte Pane-Modul. ctx: KauflandListCtx ist strukturkompatibel
  // zu DetailPaneCtx (gleiche Felder: supabase, session, supabaseUrl).
  function showDetail(id: string) {
    const detailHost = host.querySelector<HTMLDivElement>('#klst-detail')!;
    void renderListingDetailPane(detailHost, ctx, id, {
      kind: 'lister',
      onListingChanged: () => void reloadList(),
      onDeleted: () => {
        activeListingId = null;
        clearDetail('Wähle ein Listing aus der Liste links.');
        void reloadList();
      },
    });
  }

  // ---------------------------------------------------------------------
  async function reloadList() {
    const listHost = host.querySelector<HTMLDivElement>('#klst-list')!;
    listHost.innerHTML = `<div class="li-loading">Lade…</div>`;
    const { data, error } = await ctx.supabase.rpc('kaufland_list_listings', {
      _status: state.status || null,
      _storefront: state.storefront || null,
      _limit: PAGE_SIZE,
      _offset: activePage * PAGE_SIZE,
    });
    if (error) {
      listHost.innerHTML = `<div class="li-loading err">Fehler: ${escapeHtml(error.message)}</div>`;
      updatePager(0);
      return;
    }
    lastRows = (data ?? []) as ListingRow[];
    // Mode-Hydration: die RPC liefert kaufland.listings.mode nicht, also
    // ein einzelner select fuer alle IDs der aktuellen Page (max PAGE_SIZE Rows).
    // Fail-soft: bei Lookup-Error bleiben alle Rows ohne mode (→ Default-Pill "Neu").
    if (lastRows.length > 0) {
      const ids = lastRows.map((r) => r.id);
      const { data: modeRows, error: modeErr } = await ctx.supabase
        .schema('kaufland')
        .from('listings')
        .select('id, mode')
        .in('id', ids);
      if (modeErr) {
        console.warn('[kaufland-list] mode hydration failed:', modeErr.message);
      } else if (modeRows) {
        const byId = new Map<string, string>();
        for (const m of modeRows as Array<{ id: string; mode: string }>) {
          if (typeof m.mode === 'string') byId.set(m.id, m.mode);
        }
        lastRows = lastRows.map((r) => ({
          ...r,
          mode: (byId.get(r.id) === 'attach' ? 'attach' : 'new') as 'new' | 'attach',
        }));
      }
    }
    // Pager-Total VOR Mode-Filter abgreifen — das RPC zaehlt unfiltered, der
    // Filter ist clientseitig. Folge: eine Seite mit aktivem Mode-Filter kann
    // weniger Rows zeigen als PAGE_SIZE. Sauberer waere _mode_filter im RPC
    // (Migration 0116, TODO in IMPLEMENTATION.md).
    const total = lastRows[0]?.total_count ?? 0;
    if (state.mode !== 'all') {
      lastRows = lastRows.filter((r) => (r.mode ?? 'new') === state.mode);
    }
    renderMasterList(listHost, lastRows, activeListingId);
    updatePager(total);

    // Click-Wiring
    listHost.querySelectorAll<HTMLElement>('[data-listing-id]').forEach((el) => {
      el.addEventListener('click', () => {
        const id = el.dataset['listingId'];
        if (!id) return;
        activeListingId = id;
        listHost.querySelectorAll('.li').forEach((r) => r.classList.remove('sel'));
        el.classList.add('sel');
        showDetail(id);
      });
    });

    // Aktiven Such-Filter nach Reload erneut anwenden — sonst zeigt der frische
    // Render alle Rows, obwohl der User noch einen Term im Suchfeld stehen hat.
    if (state.search) applyClientSearchFilter(host, state.search);

    // Auto-Focus aus localStorage: wenn der User aus dem Vario-Slot via "Im Lister
    // bearbeiten" hierhergeschaltet hat, steht in `kaufland.focus` welches Listing
    // gemeint ist. localStorage funktioniert als Bruecke zwischen Slot-iframe und
    // Lister-Tab, weil beide auf derselben App-Origin laufen — Vario reicht eigene
    // URL-Params nicht an die iframe-URL durch.
    const focusListingId = consumeFocusListingId();
    if (focusListingId) {
      const hit = lastRows.find((r) => r.vario_listing_id === focusListingId);
      if (hit) {
        activeListingId = hit.id;
        listHost.querySelector<HTMLElement>(`[data-listing-id="${hit.id}"]`)?.classList.add('sel');
        showDetail(hit.id);
        return;
      }
      // Listing nicht auf der aktuellen Seite — Server-Lookup ueber alle Status hinweg.
      void resolveListingByVarioListingId(focusListingId).then((resolved) => {
        if (resolved) {
          activeListingId = resolved;
          showDetail(resolved);
        } else {
          // Listing existiert nicht (oder ist archived und der aktuelle Status-Filter
          // schließt es aus). Fallback auf erste Row, damit der Tab nicht leer ist.
          if (lastRows[0]) {
            activeListingId = lastRows[0].id;
            listHost.querySelector<HTMLElement>(`[data-listing-id="${lastRows[0].id}"]`)?.classList.add('sel');
            showDetail(lastRows[0].id);
          }
        }
      });
      return;
    }

    // Auto-Select erste Row, wenn nichts aktiv
    if (!activeListingId && lastRows[0]) {
      activeListingId = lastRows[0].id;
      const firstEl = listHost.querySelector<HTMLElement>(`[data-listing-id="${lastRows[0].id}"]`);
      firstEl?.classList.add('sel');
      showDetail(lastRows[0].id);
    } else if (activeListingId) {
      showDetail(activeListingId);
    } else {
      clearDetail('Wähle ein Listing aus der Liste links.');
    }
  }

  /**
   * Liest `kaufland.focus` aus localStorage und entfernt den Eintrag. Nur Eintraege
   * juenger als 30s werden akzeptiert, damit stehengebliebene Werte (Tab-Switch,
   * Browser-Reload nach Stunden) den User nicht erneut zwingen.
   */
  function consumeFocusListingId(): string | null {
    try {
      const raw = localStorage.getItem('kaufland.focus');
      if (!raw) return null;
      localStorage.removeItem('kaufland.focus');
      const parsed = JSON.parse(raw) as { listingId?: string; ts?: number };
      if (!parsed?.listingId || typeof parsed.listingId !== 'string') return null;
      if (typeof parsed.ts === 'number' && Date.now() - parsed.ts > 30_000) return null;
      return parsed.listingId;
    } catch {
      return null;
    }
  }

  /**
   * Server-side Lookup einer Listing-Row anhand vario_listing_id, falls sie nicht
   * in der aktuell geladenen Page steckt (z.B. andere Status oder weiter hinten).
   * Setzt KEINEN Filter-State zurueck; lädt nur die Detail-ID.
   */
  async function resolveListingByVarioListingId(varioListingId: string): Promise<string | null> {
    const { data, error } = await ctx.supabase
      .schema('kaufland')
      .from('listings')
      .select('id')
      .eq('vario_listing_id', varioListingId)
      .maybeSingle();
    if (error) {
      console.warn('[kaufland-list] focus-resolve failed:', error.message);
      return null;
    }
    return (data as { id?: string } | null)?.id ?? null;
  }

  function updatePager(total: number) {
    const info = host.querySelector<HTMLElement>('#klst-pg-info')!;
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    info.textContent = `${activePage * PAGE_SIZE + 1}–${Math.min(total, (activePage + 1) * PAGE_SIZE)} von ${total} · Seite ${activePage + 1} / ${pages}`;
    pgFirst.disabled = activePage === 0;
    pgPrev.disabled = activePage === 0;
    pgNext.disabled = activePage + 1 >= pages;
    pgLast.disabled = activePage + 1 >= pages;
  }

  function clearDetail(msg: string) {
    const detailHost = host.querySelector<HTMLDivElement>('#klst-detail')!;
    detailHost.innerHTML = `<div class="li-loading">${escapeHtml(msg)}</div>`;
  }

}

// ---------------------------------------------------------------------------
// Rendering Helpers
// ---------------------------------------------------------------------------

function renderShell(standalone: boolean): string {
  const apphead = standalone
    ? `<header class="apphead">
        <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">${KF_LOGO_GOECOMMERCE}</a>
        <span class="kf-logo" title="Kaufland.de">${KF_LOGO_KAUFLAND}</span>
        <span class="hdivider"></span>
        <div class="htitle"><div class="heyebrow">Kaufland · Lister</div><h1>Listing-Übersicht</h1></div>
        <div class="hactions"><span class="hstatus"><span class="d"></span>Kaufland verbunden</span></div>
      </header>`
    : '';

  const filterbar = `<div class="filterbar">
      <div class="select"><select id="klst-sf" title="Storefront filtern"></select></div>
      <div class="select"><select id="klst-status" title="Status filtern"></select></div>
      <div class="select"><select id="klst-mode" title="Modus filtern"></select></div>
      <div class="tbsearch">${KF_ICON.search}<input id="klst-search" type="search" placeholder="Listing nach SKU, Name oder Kategorie suchen…" autocomplete="off" /><button type="button" id="klst-search-clear" class="clearbtn" aria-label="Suche leeren" title="Suche leeren">${KF_ICON.close}</button></div>
      <span class="slot-note">${SPARK_ICON}Inbox wird aus Vario befüllt</span>
    </div>`;

  const work = `<div class="work">
      <div class="card listcard">
        <div id="klst-list" class="li-scroll"></div>
        <div class="li-foot">
          <button type="button" class="pgbtn" id="klst-pg-first" title="Erste Seite">${PG_FIRST}</button>
          <button type="button" class="pgbtn" id="klst-pg-prev" title="Vorherige Seite">${PG_PREV}</button>
          <span id="klst-pg-info">–</span>
          <button type="button" class="pgbtn" id="klst-pg-next" title="Nächste Seite">${PG_NEXT}</button>
          <button type="button" class="pgbtn" id="klst-pg-last" title="Letzte Seite">${PG_LAST}</button>
        </div>
      </div>
      <div class="card detailcard"><div id="klst-detail" class="kl-detail-host"></div></div>
    </div>`;

  // Standalone: voll im Sales-Channels-Tab (eigenes apphead, Höhe an iframe gekoppelt).
  // Embedded (Dashboard-Vorschau-Shell): kein apphead, feste Master-Detail-Höhe.
  return standalone
    ? `<div class="kf-page" style="height:100%;display:flex;flex-direction:column;"><main class="main">${apphead}${filterbar}${work}</main></div>`
    : `<div class="kf-page" style="display:flex;flex-direction:column;height:min(78vh,840px);padding:2px;">${filterbar}${work}</div>`;
}

async function populateStorefronts(ctx: KauflandListCtx, host: HTMLElement) {
  const sel = host.querySelector<HTMLSelectElement>('#klst-sf')!;
  sel.innerHTML = `<option value="">Alle Märkte</option>`;
  const { data, error } = await ctx.supabase.rpc('kaufland_list_storefronts');
  if (error) {
    console.warn('[kaufland-list] storefront list failed', error);
    return;
  }
  const rows = (data ?? []) as Array<{ storefront: string }>;
  for (const r of rows) {
    const label = STOREFRONT_LABELS[r.storefront] ?? r.storefront.toUpperCase();
    const opt = document.createElement('option');
    opt.value = r.storefront;
    opt.textContent = `${r.storefront.toUpperCase()} · ${label}`;
    sel.append(opt);
  }
}

function renderMasterList(host: HTMLElement, rows: ListingRow[], activeId: string | null): void {
  if (rows.length === 0) {
    host.innerHTML = `
      <div class="li-empty">
        <div class="li-empty-title">Keine Listings in dieser Ansicht</div>
        <div class="li-empty-hint">Trage in Vario am Artikel ein Listing mit Kaufland-Verkaufskanal und Kategorie ein.</div>
      </div>`;
    return;
  }
  host.innerHTML = rows.map((r) => renderMasterRow(r, r.id === activeId)).join('');
}

function renderMasterRow(r: ListingRow, active: boolean): string {
  const stCls = STATUS_CLS[r.status] ?? 'st-pending';
  const stLabel = (STATUS_PILL[r.status] ?? STATUS_PILL.pending).label;
  const sfCode = r.storefront.toUpperCase();
  const number = escapeHtml(r.vario_article_number ?? r.vario_article_id);
  const name = escapeHtml(r.vario_article_name ?? '—');
  const cat = escapeHtml(r.vario_category_name ?? '—');
  // Mode-/Variantenvater-Pill: Variantenvater (lila) ist der Sonderfall ohne
  // Mapping-Flow; sonst 'attach' (grün, reduzierter Aufschalt-Flow) bzw. 'new' (grau).
  const modePill = r.is_variant_parent
    ? `<span class="pill vp-pill">Variantenvater</span>`
    : (r.mode === 'attach'
        ? `<span class="pill mode-pill attach">Aufschalten</span>`
        : `<span class="pill mode-pill">Neu</span>`);
  const errLine = (r.status === 'error' && r.last_error)
    ? `<div class="li-err">${WARN_ICON}<span>${escapeHtml(truncate(r.last_error, 140))}</span></div>`
    : '';
  // Such-Index: alles was matchen darf in eine lowercase-Zeile.
  const searchHaystack = [
    r.vario_article_number ?? '',
    r.vario_article_id ?? '',
    r.vario_article_name ?? '',
    r.vario_category_name ?? '',
  ].join(' ').toLowerCase();
  return `
    <button type="button" class="li ${active ? 'sel' : ''}" data-listing-id="${r.id}" data-search-label="${escapeHtml(searchHaystack)}">
      <div class="li-name">${name}</div>
      <div class="li-top">
        <span class="pill ${stCls}"><span class="d"></span>${escapeHtml(stLabel)}</span>
        ${modePill}
      </div>
      <div class="li-sku">${number}</div>
      ${renderRowMiniProgress(r.status)}
      <div class="li-meta">
        <span class="li-cat">${TAG_ICON}${cat}</span>
        <span class="li-market"><span class="flag">${kfFlagSvg(r.storefront)}</span>${escapeHtml(sfCode)}</span>
      </div>
      ${errLine}
    </button>
  `;
}

/** Client-seitiger Live-Such-Filter ueber die Master-Rows. data-search-label
 *  ist ein lowercase-Index aus number+id+name+category. Term wird in tokens
 *  zerlegt, jedes token muss matchen (AND). */
function applyClientSearchFilter(host: HTMLElement, term: string): void {
  const tokens = term.trim().toLowerCase().split(/\s+/).filter((t) => t.length > 0);
  host.querySelectorAll<HTMLElement>('.li').forEach((row) => {
    if (tokens.length === 0) { row.hidden = false; return; }
    const hay = row.dataset['searchLabel'] ?? '';
    row.hidden = !tokens.every((t) => hay.includes(t));
  });
}

/** Mini-Progress unter Listing-Card. Status-basiert, weil der RPC bisher keinen
 *  mapped/required-Count liefert. Heuristik:
 *   - published/queued/mapped → 100% gruen
 *   - mapping/error           → 50% orange
 *   - pending/archived        → 0% (nur grauer track) */
function renderRowMiniProgress(status: ListingStatus): string {
  let pct = 0;
  let label = 'neu';
  switch (status) {
    case 'published':
    case 'queued':
    case 'mapped':
      pct = 100; label = 'bereit'; break;
    case 'mapping':
    case 'error':
      pct = 50; label = 'in Arbeit'; break;
    default:
      pct = 0; label = 'neu'; break;
  }
  return `
    <div class="li-prog" title="Status: ${status}">
      <div class="li-bar"><i style="width:${pct}%;"></i></div>
      <span>${label}</span>
    </div>
  `;
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
